import React, { useState, useEffect, useCallback, useRef } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { Button, Input, Typography, Flex } from "antd"
import { SendOutlined, EllipsisOutlined, RobotOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { toaster } from "components/common/Toast"
import {
  aiDashboardChat,
  getAIDashboardChatHistory,
  saveAIDashboardChartFromChat
} from "store/aiDashboard"
import { getInitialChatMessages } from "../constants"
import { mapHistoryToMessages } from "../utils"
import { GenericChartWidget } from "../widgets"
import styles from "../style.module.css"

const { Text } = Typography
const CHAT_SESSION_KEY = "dash_chat_session_id"

// ── SaveToDashboardInline ─────────────────────────────────────────────────────
function SaveToDashboardInline({ payload, dispatch, onSaved, onDismiss }) {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSave = () => {
    setSaving(true)
    setError(null)
    dispatch(saveAIDashboardChartFromChat({
      source_prompt: payload.source_prompt,
      chart_type: payload.chart_type
    }))
      .then(() => {
        toaster.success(t("ai_dashboard.chart_saved"))
        onSaved?.()
      })
      .catch((err) => {
        const apiError = err?.data?.error
        if (err?.status === 409) {
          setError(apiError || t("ai_dashboard.chart_already_saved"))
        } else {
          toaster.error(apiError || t("ai_dashboard.chart_save_failed"))
        }
        setSaving(false)
      })
  }

  return (
    <div className={styles.chatChartPrompt}>
      <Text strong className={styles.chatChartPromptText}>{t("ai_dashboard.add_chart_prompt")}</Text>
      {error && <Text type="danger" className={`${styles.textSm} ${styles.chatChartPromptText}`}>{error}</Text>}
      <Flex gap={8} className={styles.chatChartActions}>
        <Button shape="round" loading={saving} onClick={handleSave}>{t("ai_dashboard.yes_add")}</Button>
        <Button shape="round" onClick={onDismiss} disabled={saving}>{t("ai_dashboard.no_thanks")}</Button>
      </Flex>
    </div>
  )
}

SaveToDashboardInline.propTypes = {
  payload: PropTypes.object,
  dispatch: PropTypes.func,
  onSaved: PropTypes.func,
  onDismiss: PropTypes.func,
}

// ── ChatMessage ───────────────────────────────────────────────────────────────
function ChatMessage({ msg, dispatch, onChartSaved, onChartDismiss, onWidgetSaved, onOptionSelect }) {
  const { t } = useTranslation()
  return (
    <div className={`${styles.chatMsg} ${msg.sender === "user" ? styles.chatMsgUser : ""}`}>
      {msg.sender === "ai" && (
        <div className={styles.dashAvatarSm}>
          <RobotOutlined className={styles.chatAvatarIconSm} />
        </div>
      )}
      <div className={styles.chatMsgInner}>
        <div className={`${styles.chatBubble} ${msg.sender === "user" ? styles.chatBubbleUser : styles.chatBubbleAi}`}>
          {msg.text}
        </div>
        {msg.options?.items?.length > 0 && (
          <div className={styles.chatOptionTiles}>
            {msg.options.items.map((opt) => (
              <button key={opt.value} className={styles.chatOptionTile} onClick={() => onOptionSelect?.(opt)}>
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {msg.chartPayload && (
          <>
            {msg.chartPayload.chart_data != null && (
              <div className={styles.chatChartPreview}>
                <GenericChartWidget data={msg.chartPayload} periodLabel="" />
              </div>
            )}
            <SaveToDashboardInline
              payload={msg.chartPayload}
              dispatch={dispatch}
              onSaved={() => { onChartSaved(); onWidgetSaved?.() }}
              onDismiss={onChartDismiss}
            />
          </>
        )}
        {msg.widgetSaved && (
          <Text type="secondary" className={styles.textSm}>✓ {t("ai_dashboard.chart_saved")}</Text>
        )}
        {msg.actions && (
          <Flex wrap gap={6} className={styles.chatActions}>
            {msg.actions.map((a) => (
              <Button key={a} size="small" className={styles.chatActionBtn}>{a}</Button>
            ))}
          </Flex>
        )}
        <Text type="secondary" className={styles.chatMsgTime}>{msg.time}</Text>
      </div>
    </div>
  )
}

ChatMessage.propTypes = {
  msg: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  onChartSaved: PropTypes.func.isRequired,
  onChartDismiss: PropTypes.func.isRequired,
  onWidgetSaved: PropTypes.func,
  onOptionSelect: PropTypes.func,
}

// ── DashChatWidget ────────────────────────────────────────────────────────────
export default function DashChatWidget({ onWidgetSaved }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState(() => getInitialChatMessages(t))
  const [loading, setLoading] = useState(false)
  const sessionIdRef = useRef(localStorage.getItem(CHAT_SESSION_KEY) || null)
  const chatBodyRef = useRef(null)

  const scrollToBottom = useCallback(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
  }, [])

  useEffect(() => {
    const savedSessionId = localStorage.getItem(CHAT_SESSION_KEY)
    if (!savedSessionId) return
    dispatch(getAIDashboardChatHistory(savedSessionId))
      .then((res) => {
        const history = res?.messages ?? []
        if (Array.isArray(history) && history.length > 0) {
          setMessages(mapHistoryToMessages(history))
          setTimeout(scrollToBottom, 100)
        }
      })
  }, [dispatch, scrollToBottom])

  const buildAiMessage = useCallback((data) => {
    const text = data?.message || t("ai_dashboard.chat_fallback_response")
    const isChart = data?.response_type === "chart"
    const isClarification = data?.response_type === "clarification"
    return {
      id: Date.now() + 1,
      sender: "ai",
      text,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      chartPayload: isChart
        ? { chart_title: data.chart_title, chart_type: data.chart_type, chart_data: data.chart_data, source_prompt: data.source_prompt }
        : null,
      options: isClarification ? data.options : null
    }
  }, [t])

  const persistSession = (data) => {
    if (data?.session_id) {
      sessionIdRef.current = data.session_id
      localStorage.setItem(CHAT_SESSION_KEY, data.session_id)
    }
  }

  const handleSend = () => {
    const message = input.trim()
    if (!message || loading) return

    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: message,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
    setMessages((prev) => [...prev, userMsg])
    setInput("")
    setLoading(true)

    dispatch(aiDashboardChat(message, sessionIdRef.current))
      .then((data) => {
        persistSession(data)
        setMessages((prev) => [...prev, buildAiMessage(data)])
      })
      .catch(() => {
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          sender: "ai",
          text: t("ai_dashboard.chat_error"),
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        }])
      })
      .finally(() => { setLoading(false); setTimeout(scrollToBottom, 100) })
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const handleChartSaved = useCallback((msgId) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, chartPayload: null, widgetSaved: true } : m))
  }, [])

  const handleChartDismiss = useCallback((msgId) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, chartPayload: null } : m))
  }, [])

  const handleOptionSelect = useCallback((msgId, opt) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, options: null } : m))
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: opt.label,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
    setMessages((prev) => [...prev, userMsg])
    setLoading(true)

    dispatch(aiDashboardChat(opt.value, sessionIdRef.current))
      .then((data) => {
        persistSession(data)
        setMessages((prev) => [...prev, buildAiMessage(data)])
      })
      .catch(() => {
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          sender: "ai",
          text: t("ai_dashboard.chat_error"),
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        }])
      })
      .finally(() => { setLoading(false); setTimeout(scrollToBottom, 100) })
  }, [dispatch, scrollToBottom, t, buildAiMessage])

  return (
    <div className={styles.dashChat}>
      <Flex align="center" gap={12} className={styles.dashChatHeader}>
        <div className={styles.dashAvatar}>
          <RobotOutlined className={styles.chatAvatarIcon} />
        </div>
        <div>
          <Text strong className={styles.dashName}>{t("ai_dashboard.dash_name")}</Text>
          <Text type="secondary" className={styles.textSm}>{t("ai_dashboard.dash_subtitle")}</Text>
        </div>
        <Button type="text" icon={<EllipsisOutlined />} className={styles.chatMenuBtn} />
      </Flex>

      <div className={styles.dashChatBody} ref={chatBodyRef}>
        {messages.map((msg) => (
          <ChatMessage
            key={msg.id}
            msg={msg}
            dispatch={dispatch}
            onChartSaved={() => handleChartSaved(msg.id)}
            onChartDismiss={() => handleChartDismiss(msg.id)}
            onWidgetSaved={onWidgetSaved}
            onOptionSelect={(opt) => handleOptionSelect(msg.id, opt)}
          />
        ))}
        {loading && (
          <div className={styles.chatMsg}>
            <div className={styles.dashAvatarSm}>
              <RobotOutlined className={styles.chatAvatarIconSm} />
            </div>
            <div className={`${styles.chatBubble} ${styles.chatBubbleAi}`}>
              <Text type="secondary" className={styles.textSm}>{t("ai_dashboard.thinking")}</Text>
            </div>
          </div>
        )}
      </div>

      <Flex align="center" gap={8} className={styles.dashChatInput}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("ai_dashboard.input_placeholder")}
          variant="borderless"
          className={styles.chatInputField}
          disabled={loading}
        />
        <Button type="primary" icon={<SendOutlined />} shape="round" loading={loading} onClick={handleSend}>
          {t("ai_dashboard.send")}
        </Button>
      </Flex>
    </div>
  )
}

DashChatWidget.propTypes = {
  onWidgetSaved: PropTypes.func,
}
